const snapshotKey = (snapshot, index) =>
	snapshot?.imageData || snapshot?.fileName || `snapshot-${index}`;

export const getUnsavedSnapshots = (
	assignmentSnapshots = [],
	liveCallSnapshots = [],
) => {
	const merged = [
		...(Array.isArray(assignmentSnapshots) ? assignmentSnapshots : []),
		...(Array.isArray(liveCallSnapshots) ? liveCallSnapshots : []),
	];
	const seen = new Set();

	return merged.filter((snapshot, index) => {
		if (!snapshot || snapshot.isSaved === true) return false;
		const key = snapshotKey(snapshot, index);
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
};

export const validateLossAssessment = ({
	sumInsured,
	totalArea,
	perSqFtRate,
	excess,
	totalAssessmentAfterDep,
	adjustedLoss,
}) => {
	const requiredPositiveValues = [
		["Sum insured", sumInsured],
		["Total area", totalArea],
		["Rate per square foot", perSqFtRate],
	];

	for (const [label, value] of requiredPositiveValues) {
		if (!Number.isFinite(Number(value)) || Number(value) <= 0) {
			return `${label} must be greater than zero.`;
		}
	}

	if (
		!Number.isFinite(Number(totalAssessmentAfterDep)) ||
		Number(totalAssessmentAfterDep) <= 0
	) {
		return "At least one loss item must have a positive adjusted amount.";
	}

	if (!Number.isFinite(Number(excess)) || Number(excess) < 0) {
		return "Policy excess cannot be negative.";
	}

	if (!Number.isFinite(Number(adjustedLoss)) || Number(adjustedLoss) < 0) {
		return "The adjusted loss could not be calculated.";
	}

	if (Number(excess) > Number(adjustedLoss)) {
		return "Policy excess cannot exceed the adjusted loss.";
	}

	return null;
};

export const roundMoney = (value) => {
	const amount = Number(value);
	if (!Number.isFinite(amount)) return 0;
	return Math.round((amount + Number.EPSILON) * 100) / 100;
};

export const validateRequiredClaimDates = ({ dateOfLoss, dateOfSurvey }) => {
	const errors = {};
	const isValidDate = (value) =>
		Boolean(value) && !Number.isNaN(new Date(value).getTime());

	if (!isValidDate(dateOfLoss)) {
		errors.dateOfLoss = "Date of Loss is required.";
	}
	if (!isValidDate(dateOfSurvey)) {
		errors.dateOfSurvey = "Date of Survey is required.";
	}

	return errors;
};

const TEMPLATE_BRAND_ALIASES = {
	aig: ["aig"],
	generali: ["generali"],
	lonpac: ["lonpac"],
	liberty: ["liberty"],
	pacific: ["pacific"],
	takaful: ["takaful", "syarikat"],
};

const findKnownBrand = (value = "") => {
	const tokens = value.toLowerCase().match(/[a-z0-9]+/g) || [];
	return Object.entries(TEMPLATE_BRAND_ALIASES).find(([, aliases]) =>
		aliases.some((alias) => tokens.includes(alias)),
	)?.[0];
};

export const templateMatchesInsuranceCompany = (
	templateName,
	insuranceCompany,
) => {
	const templateBrand = findKnownBrand(templateName);
	const companyBrand = findKnownBrand(insuranceCompany);
	return !templateBrand || !companyBrand || templateBrand === companyBrand;
};

export const formatTemplateMoney = (value) => {
	if (value === null || value === undefined || value === "") return "";
	const amount = Number(value);
	return Number.isFinite(amount) ? amount.toFixed(2) : "";
};
